import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

export type WorkDecision = 'approve' | 'deny';

export interface WorkEmailPreview {
  subject: string;
  body: string;
  fallback_reason?: string | null;
}

export interface WorkPendingApproval {
  batch_id?: string;
  approval_batch_id?: string;
  skill_name?: string;
  skill_version?: string;
  skill_calls?: Array<{
    skill_call_id?: string;
    skill_name?: string;
    skill_version?: string;
    arguments_preview?: { [key: string]: unknown };
  }>;
  email?: WorkEmailPreview | null;
}

export interface WorkRunView {
  run_id?: string;
  assignment_id?: string;
  status?: string;
  outcome?: string;
  version?: number;
  pending_approval?: WorkPendingApproval | null;
  failure_code?: string;
}

export const HITL_OUTCOMES = ['Waiting', 'HumanInputRequired', 'Escalated'] as const;

const STATUS_TO_OUTCOME: { [status: string]: string } = {
  waiting: 'Waiting',
  WAITING: 'Waiting',
  WAITING_FOR_EVENT: 'Waiting',
  human_input_required: 'HumanInputRequired',
  HUMAN_INPUT_REQUIRED: 'HumanInputRequired',
  WAITING_FOR_APPROVAL: 'HumanInputRequired',
  WAITING_FOR_HUMAN: 'HumanInputRequired',
  escalated: 'Escalated',
  ESCALATED: 'Escalated',
  COMPLETED: 'Completed',
  completed: 'Completed',
  FAILED: 'Failed',
  failed: 'Failed',
  CANCELLED: 'Cancelled',
  cancelled: 'Cancelled',
  TIMED_OUT: 'TimedOut',
  RUNNING: 'Running',
  running: 'Running',
};

export function publicWorkOutcome(run: WorkRunView | null): string {
  if (!run) {
    return '';
  }
  if (run.outcome) {
    return run.outcome;
  }
  if (!run.status) {
    return '';
  }
  return STATUS_TO_OUTCOME[run.status] || run.status;
}

export function isHitlPending(run: WorkRunView | null): boolean {
  const outcome = publicWorkOutcome(run);
  return outcome === 'Waiting' || outcome === 'HumanInputRequired' || outcome === 'Escalated';
}

export function pendingBatchId(run: WorkRunView | null): string | null {
  const pending = run?.pending_approval;
  if (!pending) {
    return null;
  }
  return pending.batch_id || pending.approval_batch_id || null;
}

export function pendingSkillCallIds(run: WorkRunView | null): string[] {
  const calls = run?.pending_approval?.skill_calls || [];
  return calls.map((call) => call.skill_call_id).filter((id): id is string => !!id);
}

export function hasPendingSkill(run: WorkRunView | null, skillName: string): boolean {
  const pending = run?.pending_approval;
  return (
    pending?.skill_name === skillName ||
    !!pending?.skill_calls?.some((call) => call.skill_name === skillName)
  );
}

export function pendingEmail(run: WorkRunView | null): WorkEmailPreview | null {
  const pending = run?.pending_approval;
  const email = pending?.email;
  if (email?.subject && email?.body) {
    return email;
  }

  const emailCall = pending?.skill_calls?.find(
    (call) => call.skill_name === 'Messaging.SendEngage',
  );
  const subject = emailCall?.arguments_preview?.subject;
  const body = emailCall?.arguments_preview?.body;
  if (typeof subject !== 'string' || !subject || typeof body !== 'string' || !body) {
    return null;
  }
  return { subject, body };
}

export function newDecisionId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
    16,
    20,
  )}-${hex.slice(20)}`;
}

export function workDecisionPayload(
  skillCallIds: string[],
  decision: WorkDecision,
  decisionId: string = newDecisionId(),
): { decision_id: string; decision: WorkDecision; skill_call_ids: string[] } {
  return {
    decision_id: decisionId,
    decision,
    skill_call_ids: skillCallIds,
  };
}

@Injectable({ providedIn: 'root' })
export class AgentWorkService {
  constructor(private http: HttpClient) {}

  getRun(runId: string): Observable<WorkRunView> {
    return this.http
      .get<WorkRunView | { data?: WorkRunView }>(
        `${environment.AI_GATEWAY_BASE}/v1/work/${encodeURIComponent(runId)}`,
      )
      .pipe(map((res) => this.unwrapRun(res)));
  }

  submitDecision(
    runId: string,
    skillCallIds: string[],
    decision: WorkDecision,
  ): Observable<WorkRunView> {
    return this.http
      .post<WorkRunView | { data?: WorkRunView }>(
        `${environment.AI_GATEWAY_BASE}/v1/work/${encodeURIComponent(runId)}/decision`,
        workDecisionPayload(skillCallIds, decision),
      )
      .pipe(map((res) => this.unwrapRun(res)));
  }

  private unwrapRun(res: WorkRunView | { data?: WorkRunView }): WorkRunView {
    if (res && (res as { data?: WorkRunView }).data) {
      return (res as { data: WorkRunView }).data;
    }
    return (res || {}) as WorkRunView;
  }
}
